import { Component, Inject, OnInit } from '@angular/core';
import { FormControl, FormGroup, Validators } from '@angular/forms';
import { MAT_DIALOG_DATA, MatDialogRef } from '@angular/material';
import { VendorService } from '../../vendor-portal/vendor.service';
import { FormUtils } from '../../utilities/FormUtils/FormUtils';

export type CustomerDiscountType = 'percentage' | 'flat' | null;

export interface CustomerDiscountDialogData {
  name?: string;
  savedid?: number | string;
  customerid?: number | string;
  account_number?: string;
  discount_type?: CustomerDiscountType;
  discount_value?: number | null;
}

@Component({
  selector: 'app-set-customer-discount',
  templateUrl: './set-customer-discount.component.html',
  styleUrls: ['./set-customer-discount.component.css']
})
export class SetCustomerDiscountComponent implements OnInit {

  form: FormGroup;
  customer: CustomerDiscountDialogData;
  errorMessage = '';
  isSubmitting = false;
  isClearing = false;
  hasExistingDiscount = false;

  constructor(
    public dialogRef: MatDialogRef<SetCustomerDiscountComponent>,
    @Inject(MAT_DIALOG_DATA) public data: CustomerDiscountDialogData,
    private vendor: VendorService
  ) {
    this.customer = data || {};
    this.hasExistingDiscount = !!(this.customer.discount_type && this.customer.discount_value != null);
    this.form = new FormGroup({
      discount_type: new FormControl(this.customer.discount_type || 'percentage', [Validators.required]),
      discount_value: new FormControl(
        this.customer.discount_value != null ? this.customer.discount_value : null,
        [Validators.required, Validators.min(0)]
      )
    });
  }

  ngOnInit() {
    this.form.get('discount_type').valueChanges.subscribe(() => {
      this.errorMessage = '';
      this.form.get('discount_value').updateValueAndValidity();
    });
  }

  get isPercentage(): boolean {
    return this.form.get('discount_type').value === 'percentage';
  }

  close() {
    this.dialogRef.close();
  }

  isFieldInvalid(name: string): boolean {
    const field = this.form.get(name);
    return !!(field && field.invalid && field.touched);
  }

  getFieldErrorMessage(name: string): string {
    const field = this.form.get(name);
    if (!field || !(field.invalid && field.touched)) {
      return '';
    }
    if (field.errors && field.errors['required']) {
      return name === 'discount_value' ? 'Discount value is required.' : 'Discount type is required.';
    }
    if (field.errors && field.errors['min']) {
      return 'Value must be 0 or greater.';
    }
    return '';
  }

  private extractErrorMessage(e: any, fallback: string): string {
    try {
      if (e && e._body) {
        const body = typeof e._body === 'string' ? JSON.parse(e._body) : e._body;
        if (body && body.message) {
          return body.message;
        }
      }
      if (e && e.error && e.error.message) {
        return e.error.message;
      }
      if (e && e.message) {
        return e.message;
      }
    } catch (err) {
      // ignore
    }
    return fallback;
  }

  private buildOwnerPayload() {
    if (this.customer.savedid != null && this.customer.savedid !== '') {
      return { savedid: this.customer.savedid };
    }
    if (this.customer.customerid != null && this.customer.customerid !== '') {
      return { customerid: this.customer.customerid };
    }
    return null;
  }

  save() {
    FormUtils.markFormGroupTouched(this.form);
    this.errorMessage = '';

    if (!this.form.valid) {
      return;
    }

    const type = this.form.get('discount_type').value;
    const value = Number(this.form.get('discount_value').value);

    if (isNaN(value) || value < 0) {
      this.errorMessage = 'Discount value must be a number greater than or equal to 0.';
      return;
    }
    if (type === 'percentage' && value > 100) {
      this.errorMessage = 'Percentage discount cannot exceed 100.';
      return;
    }

    const owner = this.buildOwnerPayload();
    if (!owner) {
      this.errorMessage = 'Unable to identify this customer.';
      return;
    }

    this.isSubmitting = true;
    this.vendor.setCustomerDiscount({
      ...owner,
      discount_type: type,
      discount_value: value
    }).then((res) => {
      this.isSubmitting = false;
      if (res && res.status === false) {
        this.errorMessage = res.message || 'Failed to save discount.';
        return;
      }
      const data = (res && res.data) || {};
      this.dialogRef.close({
        saved: true,
        message: (data && data.message) || (res && res.message) || 'Customer discount saved successfully',
        discount_type: data.discount_type != null ? data.discount_type : type,
        discount_value: data.discount_value != null ? data.discount_value : value
      });
    }).catch((e) => {
      this.isSubmitting = false;
      this.errorMessage = this.extractErrorMessage(e, 'Failed to save discount. Please try again.');
    });
  }

  clearDiscount() {
    if (!this.hasExistingDiscount || this.isClearing || this.isSubmitting) {
      return;
    }
    const owner = this.buildOwnerPayload();
    if (!owner) {
      this.errorMessage = 'Unable to identify this customer.';
      return;
    }

    this.isClearing = true;
    this.errorMessage = '';
    this.vendor.clearCustomerDiscount(owner).then((res) => {
      this.isClearing = false;
      if (res && res.status === false) {
        this.errorMessage = res.message || 'Failed to clear discount.';
        return;
      }
      const data = (res && res.data) || {};
      this.dialogRef.close({
        cleared: true,
        message: (data && data.message) || (res && res.message) || 'Customer discount cleared successfully',
        discount_type: null,
        discount_value: null
      });
    }).catch((e) => {
      this.isClearing = false;
      this.errorMessage = this.extractErrorMessage(e, 'Failed to clear discount. Please try again.');
    });
  }
}
